import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { TasksActions } from 'state/modules/tasks';
import { forceTimezone, diffObjects } from 'utils';
import { UsersSelect } from 'components/AsyncFields';
import { useDispatch, useSelector } from 'react-redux';
import { PrioritySelect } from 'components/DataFields';
import { TextField, TextareaField, DateTimePickerField } from 'components/FormFields';

const StyledRelatedToText = styled(Box)(({ theme }) => ({
  backgroundColor: '#EEEEEE',
  borderRadius: 11,
  color: theme.palette.text.subtitle,
  display: 'inline-block',
  marginTop: theme.spacing(1.25),
  padding: '5px 18px',
  textTransform: 'capitalize',
}));

const CreateTaskModal = ({ open, id, type, handleClick, current }) => {
  const formSchema = Yup.object().shape({
    title: Yup.string().required(i18n.t('common.required_field')),
    owner_user_id: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string().required(i18n.t('common.required_field')),
    priority: Yup.string().required(i18n.t('common.required_field')),
    due_time: Yup.string().required(i18n.t('common.required_field')),
  });

  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { localeFormatLower } = useSelector((state) => state.organizations);
  const { fetchingCreateTask, fetchingUpdateTask } = useSelector((state) => state.tasks);

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const initialValues = {
    title: current?.title || '',
    priority: current?.priority || '',
    description: current?.description || '',
    owner_user_id: current?.owner_user?.id || '',
    due_time: current?.due_time || moment().add(1, 'hour'),
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      enterButtonLabel={i18n.t('common.save')}
      loading={fetchingCreateTask || fetchingUpdateTask}
      title={i18n.t(current ? 'tasks.edit' : 'tasks.create')}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={(values) => {
          const { due_time } = values;
          let payload = diffObjects(initialValues, values);
          payload = {
            ...payload,
            due_time,
            object_id: id,
            object_class: type,
          };
          if (payload.due_time) {
            payload.due_time = forceTimezone(moment(values.due_time));
          }
          if (current) {
            dispatch(TasksActions.fetchUpdateTask(current?.id, payload, handleClick));
          } else {
            dispatch(TasksActions.fetchCreateTask(payload, handleClick));
          }
        }}
        validationSchema={formSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit} autoComplete="off">
            <Grid data-cy="create-task-modal" container spacing={2} sx={{ m: 0, width: '100%' }}>
              <Grid size={{ xs: 6 }}>
                <Field
                  type="text"
                  name="title"
                  margin="none"
                  component={TextField}
                  label={i18n.t('common.title')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Field
                  margin="none"
                  type="assigned"
                  name="owner_user_id"
                  component={UsersSelect}
                  id="tasksAssignedSelect"
                  label={i18n.t('tasks.assigned_to')}
                  customParams={{ is_customer: false }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Field
                  margin="none"
                  name="priority"
                  component={PrioritySelect}
                  label={i18n.t('common.priority')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Field
                  utc={true}
                  margin="none"
                  editable={true}
                  name="due_time"
                  minDate={new Date()}
                  showTimeSelect={true}
                  popperPlacement="bottom-end"
                  dateFormat={`${localeFormatLower} h:mm a`}
                  component={DateTimePickerField}
                  label={i18n.t('common.due_time')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Field
                  rows={2}
                  margin="none"
                  name="description"
                  variant="standard"
                  component={TextareaField}
                  label={i18n.t('common.description')}
                />
              </Grid>
              {type && id && (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="inherit">
                      <Box
                        component="span"
                        sx={{
                          fontSize: 13,
                          fontWeight: 'fontWeightBold',
                          color: '#4D4F5C',
                        }}
                      >
                        {i18n.t('tasks.related_to')}
                      </Box>
                    </Typography>
                    <Typography variant="inherit">
                      <StyledRelatedToText
                        component="span"
                        sx={{ fontSize: 11, fontWeight: 'fontWeightLight' }}
                      >
                        {type} ID#{id}
                      </StyledRelatedToText>
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

CreateTaskModal.propTypes = {
  current: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
};

CreateTaskModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default CreateTaskModal;
